import { useState, useEffect, useRef, useCallback } from "react"
import { useAuth } from "@clerk/clerk-react"

function useAccessToken() {
  const [loadingToken, setLoadingToken] = useState(false)
  const [idToken, setIdToken] = useState(null)
  const { getToken, isLoaded } = useAuth()
  const refreshTimerRef = useRef(null)
  const tokenRef = useRef(null)

  const scheduleRefresh = useCallback((claims) => {
    // Schedule a refresh shortly before expiry (10s buffer)
    if (claims.exp) {
      const expiryMs = claims.exp * 1000
      const refreshAt = Math.max(expiryMs - Date.now() - 10000, 0)
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      // Only schedule if there's time remaining
      if (refreshAt > 0) {
        refreshTimerRef.current = setTimeout(() => {
          // Silently refresh token without updating state to avoid Apollo client recreation
          performSilentRefresh().catch(err => console.error('Failed scheduled token refresh', err))
        }, refreshAt)
        console.log(`Scheduled token refresh in ${Math.round(refreshAt/1000)}s`)
      }
    }
  }, [])

  const performSilentRefresh = useCallback(async () => {
    if (!isLoaded) return

    try {
      // Get the new token
      const token = await getToken({ template: 'hasura' })

      console.log('🔑 Token refreshed silently')

      if (token) {
        try {
          // Decode the JWT to read expiry and claims (no verification)
          const base64Url = token.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''))
          const claims = JSON.parse(jsonPayload)
          
          // Store new token in ref without triggering state update
          tokenRef.current = token
          
          // Schedule next refresh
          scheduleRefresh(claims)
        } catch (decodeError) {
          console.error('Failed to decode JWT:', decodeError)
        }
      }
    } catch (e) {
      console.log('Silent refresh error:', e.message)
    }
  }, [getToken, isLoaded, scheduleRefresh])

  const getAccessToken = useCallback(async () => {
    if (!isLoaded) return

    setLoadingToken(true)

    try {
      // Get the token with the Hasura JWT template configured in Clerk Dashboard
      const token = await getToken({ template: 'hasura' })

      console.log('🔑 JWT Token:', token)

      if (token) {
        try {
          // Decode the JWT to read expiry and claims (no verification)
          const base64Url = token.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''))
          const claims = JSON.parse(jsonPayload)
          console.log('📋 JWT Claims:', claims)
          console.log('🔐 Hasura Claims:', claims['https://hasura.io/jwt/claims'])

          tokenRef.current = token
          scheduleRefresh(claims)
        } catch (decodeError) {
          console.error('Failed to decode JWT:', decodeError)
        }
      }

      setIdToken(token)
    } catch (e) {
      console.log(e.message)
    }

    setLoadingToken(false)
  }, [getToken, isLoaded, scheduleRefresh])

  useEffect(() => {
    // Initial fetch when Clerk is ready
    if (!isLoaded) return
    getAccessToken()

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [getAccessToken, isLoaded])

  const refreshToken = useCallback(async () => {
    return getAccessToken()
  }, [getAccessToken])

  return [idToken, loadingToken, refreshToken]
}

export default useAccessToken
